import { useEffect, useRef, useState } from "react";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Smartphone, Plus, Pencil, Trash2, ImagePlus, X, Loader2, Star, Users, MessageCircle, CheckCircle2, Package, FileText, Calculator } from "lucide-react";
import { FinanceConfigTab, ManualFinance } from "@/components/BoletoFinanceConfig";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 5;
const imgUrl = (p) => (!p ? "" : p.startsWith("http") ? p : `${BACKEND}${p}`);
const empty = { brand: "", model: "", storage: "", color: "", condition: "Novo", price: "", description: "", installments: 12, entry: "", photos: [], featured: false, active: true, tag_easy: false, tag_credit: false, manualFinance: false, manualEntry: "", manualInstallments: {} };

export default function BoletoAdmin() {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("devices");
  const canManage = hasPerm("device_sale.create") || hasPerm("device_sale.edit");

  return (
    <div className="space-y-6" data-testid="boleto-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-extrabold tracking-tight text-white">Celular no Boleto</h1>
          <p className="text-zinc-400 mt-1">Vitrine de aparelhos no boleto e leads de interessados (financiamento via financeiras parceiras).</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button data-testid="boleto-tab-devices" onClick={() => setTab("devices")} className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === "devices" ? "bg-gold/15 border-gold/30 text-gold-bright" : "border-white/10 text-zinc-400 hover:text-white"}`}><Smartphone size={15} className="inline mr-1.5" />Aparelhos</button>
        <button data-testid="boleto-tab-leads" onClick={() => setTab("leads")} className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === "leads" ? "bg-gold/15 border-gold/30 text-gold-bright" : "border-white/10 text-zinc-400 hover:text-white"}`}><Users size={15} className="inline mr-1.5" />Leads</button>
        <button data-testid="boleto-tab-contracts" onClick={() => setTab("contracts")} className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === "contracts" ? "bg-gold/15 border-gold/30 text-gold-bright" : "border-white/10 text-zinc-400 hover:text-white"}`}><FileText size={15} className="inline mr-1.5" />Contratos</button>
        <button data-testid="boleto-tab-finance" onClick={() => setTab("finance")} className={`px-4 py-2 rounded-lg text-sm font-medium border ${tab === "finance" ? "bg-gold/15 border-gold/30 text-gold-bright" : "border-white/10 text-zinc-400 hover:text-white"}`}><Calculator size={15} className="inline mr-1.5" />Financiamento</button>
      </div>

      {tab === "devices" && <DevicesTab canManage={canManage} />}
      {tab === "leads" && <LeadsTab company={null} />}
      {tab === "contracts" && <ContractsTab canManage={canManage} />}
      {tab === "finance" && <FinanceConfigTab canManage={canManage} />}
    </div>
  );
}

function DevicesTab({ canManage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/boleto"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (d) => {
    if (!window.confirm(`Excluir "${d.brand} ${d.model}" da vitrine de boleto?`)) return;
    try { await api.delete(`/boleto/${d.id}`); toast.success("Aparelho excluído"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      {canManage && <Button data-testid="boleto-new-device" onClick={() => setEdit({ ...empty })} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}><Plus size={16} className="mr-2" />Novo Aparelho</Button>}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" /></div>
      ) : items.length === 0 ? (
        <div className="alpha-card p-12 text-center" data-testid="boleto-devices-empty">
          <Package className="mx-auto text-zinc-600" size={40} />
          <p className="text-zinc-400 mt-3">Nenhum aparelho cadastrado para o boleto.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((d) => (
            <div key={d.id} data-testid={`boleto-admin-card-${d.id}`} className="alpha-card overflow-hidden flex flex-col">
              <div className="aspect-video overflow-hidden bg-[#0A0A0C] relative">
                {d.photos?.[0] ? <img src={imgUrl(d.photos[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-700"><Smartphone size={36} /></div>}
                {d.featured && <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-gold/90 text-black font-semibold flex items-center gap-1"><Star size={10} className="fill-black" />Destaque</span>}
                {!d.active && <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/70 text-red-300">Inativo</span>}
              </div>
              <div className="p-4 flex flex-col flex-1">
                <div className="text-white font-medium">{d.brand} {d.model}</div>
                <div className="text-xs text-zinc-500">{[d.storage, d.color, d.condition].filter(Boolean).join(" · ")}</div>
                <div className="text-gold-bright font-mono-kpi mt-2">{money(d.price)}</div>
                <div className="text-xs text-zinc-400 mt-1" data-testid={`boleto-admin-finance-${d.id}`}>
                  {d.finance?.rule === "special" ? `Regra iPhone: sem simulação (consulta WhatsApp)${d.finance?.upTo24 ? " · até 24x" : ""}`
                    : d.finance?.rule === "credit" ? "Excelente perfil de crédito: só preço · até 24x"
                    : <>{d.finance?.manual ? "Manual" : "Auto"} · entrada {money(d.finance?.entry)} · {(d.finance?.installments || []).map((i) => `${i.n}x ${money(i.value)}`).join(" · ")}</>}
                </div>
                {canManage && <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
                  <button data-testid={`boleto-edit-${d.id}`} onClick={() => setEdit(d)} className="text-zinc-400 hover:text-gold-bright"><Pencil size={16} /></button>
                  <button data-testid={`boleto-del-${d.id}`} onClick={() => remove(d)} className="text-zinc-400 hover:text-red-400"><Trash2 size={16} /></button>
                </div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && <DeviceDialog device={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function ContractsTab({ canManage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/boleto/contracts"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const confirmRepasse = async (c) => {
    const input = window.prompt(`Valor recebido da ${c.financerLabel} (R$). Se a financeira descontou algo, ajuste o valor recebido:`, Number(c.total).toFixed(2));
    if (input === null) return;
    const received = parseFloat(String(input).replace(",", "."));
    if (!(received > 0)) { toast.error("Valor inválido."); return; }
    try { await api.post(`/boleto/contracts/${c.id}/confirm?received=${received}`); toast.success("Repasse confirmado — venda incluída no relatório."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "-");
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-500">Vendas pagas por PayJoy, Parcelex ou AivaPay (Ume). Elas só entram no relatório e no caixa após você confirmar o repasse da financeira. Prazos: PayJoy 7 dias · Parcelex 2 dias · AivaPay 1 dia.</p>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" /></div>
      ) : items.length === 0 ? (
        <div className="alpha-card p-12 text-center" data-testid="boleto-contracts-empty"><FileText className="mx-auto text-zinc-600" size={40} /><p className="text-zinc-400 mt-3">Nenhum contrato ainda.</p></div>
      ) : (
        <div className="grid gap-2">
          {items.map((c) => {
            const confirmed = c.repasseStatus === "confirmado";
            return (
              <div key={c.id} data-testid={`contract-${c.id}`} className="alpha-card p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">{c.description} <span className="text-xs text-zinc-500">· {c.saleNumber}</span></div>
                  <div className="text-xs text-zinc-500 flex flex-wrap gap-x-2">
                    <span className="text-gold-bright">{c.financerLabel}</span>
                    <span>· Venda {fmt(c.createdAt)}</span>
                    <span>· Repasse previsto {fmt(c.repasseDueDate)}</span>
                    {c.customerName && <span>· {c.customerName}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-mono-kpi">{money(c.total)}</div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: confirmed ? "#10B98122" : "#F59E0B22", color: confirmed ? "#10B981" : "#F59E0B" }}>{confirmed ? "Repasse confirmado" : "Aguardando repasse"}</span>
                  {confirmed && c.financeDiscount > 0 && <div className="text-[10px] text-zinc-500 mt-0.5">recebido {money(c.receivedAmount)} · desc. {money(c.financeDiscount)}</div>}
                </div>
                {canManage && !confirmed && <button data-testid={`contract-confirm-${c.id}`} onClick={() => confirmRepasse(c)} className="text-xs px-3 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold whitespace-nowrap"><CheckCircle2 size={14} className="inline mr-1" />Confirmar repasse</button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DeviceDialog({ device, onClose, onSaved }) {
  const isNew = !device.id;
  const [f, setF] = useState({ ...empty, ...device });
  const [uploading, setUploading] = useState(false);
  const [brands, setBrands] = useState([]);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => { api.get("/boleto/brands").then((r) => setBrands(r.data)).catch(() => {}); }, []);
  const brandOptions = [...new Set([...brands, ...(f.brand ? [f.brand] : [])])];

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (files.length === 0) return;
    if ((f.photos?.length || 0) + files.length > MAX_PHOTOS) { toast.error(`Máximo de ${MAX_PHOTOS} fotos.`); return; }
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/boleto/upload", fd);
        setF((prev) => ({ ...prev, photos: [...(prev.photos || []), data.url] }));
      }
      toast.success("Foto enviada");
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const removePhoto = (url) => setF((prev) => ({ ...prev, photos: (prev.photos || []).filter((p) => p !== url) }));

  const save = async () => {
    if (!f.brand || !f.model || f.price === "") { toast.error("Marca, modelo e preço são obrigatórios."); return; }
    const { finance, ...rest } = f;
    const mi = Object.fromEntries(Object.entries(f.manualInstallments || {}).map(([k, v]) => [k, parseFloat(v) || 0]));
    const payload = { ...rest, price: parseFloat(f.price) || 0, entry: parseFloat(f.entry) || 0, installments: parseInt(f.installments) || 12,
      manualFinance: !!f.manualFinance, manualEntry: parseFloat(f.manualEntry) || 0, manualInstallments: mi };
    try {
      if (isNew) await api.post("/boleto", payload);
      else await api.put(`/boleto/${device.id}`, payload);
      toast.success("Aparelho salvo"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121217] border-gold/20 text-white max-w-2xl" data-testid="boleto-device-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Novo Aparelho (Boleto)" : "Editar Aparelho"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 max-h-[62vh] overflow-y-auto pr-1">
          <div className="sm:col-span-2 border-b border-white/10 pb-3">
            <Label className="text-xs text-zinc-400 mb-1.5 block">Fotos (até {MAX_PHOTOS})</Label>
            <div className="flex flex-wrap gap-2">
              {(f.photos || []).map((url) => (
                <div key={url} className="relative h-20 w-20 rounded-lg overflow-hidden border border-white/10">
                  <img src={imgUrl(url)} alt="foto" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removePhoto(url)} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600"><X size={12} /></button>
                </div>
              ))}
              {(f.photos?.length || 0) < MAX_PHOTOS && (
                <button type="button" data-testid="boleto-add-photo" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="h-20 w-20 rounded-lg border border-dashed border-white/20 text-zinc-400 flex flex-col items-center justify-center hover:border-gold/50 hover:text-gold-bright transition-colors">
                  {uploading ? <Loader2 size={18} className="animate-spin" /> : <><ImagePlus size={18} /><span className="text-[10px] mt-1">Adicionar</span></>}
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPickFiles} className="hidden" data-testid="boleto-file-input" />
            <div className="flex items-center gap-4 gap-y-2 mt-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input type="checkbox" data-testid="boleto-featured" checked={!!f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} className="accent-[#D4AF37]" />
                <Star size={13} className="text-gold-bright" /> Destaque
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input type="checkbox" data-testid="boleto-active" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="accent-[#D4AF37]" />
                Ativo (visível na vitrine pública)
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input type="checkbox" data-testid="boleto-tag-easy" checked={!!f.tag_easy} onChange={(e) => setF({ ...f, tag_easy: e.target.checked })} className="accent-emerald-500" />
                Etiqueta "Fácil aprovação"
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input type="checkbox" data-testid="boleto-tag-credit" checked={!!f.tag_credit} onChange={(e) => setF({ ...f, tag_credit: e.target.checked })} className="accent-amber-500" />
                Etiqueta "Para cliente com excelente perfil de crédito"
              </label>
            </div>
          </div>
          <L t="Marca *"><Select value={f.brand} onValueChange={(v) => setF({ ...f, brand: v })}><SelectTrigger data-testid="boleto-brand" className="i"><SelectValue placeholder="Selecione a marca" /></SelectTrigger><SelectContent className="bg-[#121217] border-gold/20 text-white max-h-72">{brandOptions.map((b) => <SelectItem key={b} value={b} className="focus:bg-gold/10">{b}</SelectItem>)}</SelectContent></Select></L>
          <L t="Modelo *"><Input data-testid="boleto-model" value={f.model} onChange={set("model")} className="i" /></L>
          <L t="Armazenamento"><Input value={f.storage} onChange={set("storage")} className="i" placeholder="128GB" /></L>
          <L t="Cor"><Input value={f.color} onChange={set("color")} className="i" /></L>
          <L t="Condição"><Select value={f.condition} onValueChange={(v) => setF({ ...f, condition: v })}><SelectTrigger data-testid="boleto-condition" className="i"><SelectValue /></SelectTrigger><SelectContent className="bg-[#121217] border-gold/20 text-white">{["Novo", "Seminovo"].map((c) => <SelectItem key={c} value={c} className="focus:bg-gold/10">{c}</SelectItem>)}</SelectContent></Select></L>
          <L t="Preço à vista (R$) *"><Input data-testid="boleto-price" type="number" value={f.price} onChange={set("price")} className="i" /></L>
          <div className="text-[11px] text-zinc-500 self-end pb-2">Entrada e parcelas são calculadas automaticamente (aba Financiamento). iPhones/Apple não recebem simulação.</div>
          <ManualFinance f={f} setF={setF} />
          <div className="sm:col-span-2"><L t="Descrição"><Textarea value={f.description} onChange={set("description")} className="i" rows={2} /></L></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancelar</Button>
          <Button data-testid="boleto-device-save" onClick={save} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [l, h] = await Promise.all([api.get("/boleto/leads"), api.get("/public/boleto").catch(() => ({ data: {} }))]);
      setLeads(l.data); setCompany(h.data?.company || {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (lead) => {
    try { await api.put(`/boleto/leads/${lead.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const waLink = (lead) => {
    const wa = (company?.whatsapp || "").replace(/\D/g, "");
    const msg = `Olá ${lead.fullName}! Aqui é da Alpha Prime Cell sobre o ${lead.deviceName || "celular no boleto"} que você demonstrou interesse.`;
    return `https://wa.me/${wa}?text=${encodeURIComponent(msg)}`;
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" /></div>;
  if (leads.length === 0) return (
    <div className="alpha-card p-12 text-center" data-testid="boleto-leads-empty">
      <Users className="mx-auto text-zinc-600" size={40} />
      <p className="text-zinc-400 mt-3">Nenhum lead recebido ainda.</p>
    </div>
  );

  return (
    <div className="grid gap-2" data-testid="boleto-leads-list">
      {leads.map((l) => (
        <div key={l.id} data-testid={`boleto-lead-${l.id}`} className="alpha-card p-4 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="text-white font-medium flex items-center gap-2">
              {l.fullName}
              {l.status === "contatado" && <span className="text-[10px] text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 size={11} />contatado</span>}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
              <span>{l.phone}</span>
              {l.cpf && <span>CPF {l.cpf}</span>}
              {l.email && <span>{l.email}</span>}
              {l.cep && <span>CEP {l.cep}</span>}
            </div>
            {l.deviceName && <div className="text-xs text-gold-bright/80 mt-1">Interesse: {l.deviceName}</div>}
            {l.message && <div className="text-xs text-zinc-400 mt-1 italic">"{l.message}"</div>}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid={`boleto-lead-toggle-${l.id}`} onClick={() => toggle(l)} className={`text-xs px-3 py-1.5 rounded-lg border ${l.status === "contatado" ? "border-white/10 text-zinc-400" : "border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"}`}>
              {l.status === "contatado" ? "Reabrir" : "Marcar contatado"}
            </button>
            <a href={waLink(l)} target="_blank" rel="noreferrer" data-testid={`boleto-lead-wa-${l.id}`} className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white text-xs px-3 py-1.5 rounded-lg font-medium"><MessageCircle size={14} />WhatsApp</a>
          </div>
        </div>
      ))}
    </div>
  );
}

const L = ({ t, children }) => (<div><Label className="text-xs text-zinc-400 mb-1.5 block">{t}</Label>{children}</div>);
